import transparentize from 'polished/lib/color/transparentize';
import { RefObject } from 'react';
import styled, { css } from 'styled-components';

import { Color } from '@tmh-website/shared/colors';
import StyledTypo from '@tmh-website/styles/typo';
import { StyledFormElements } from '@tmh-website/ui/FormElements';

import { DropdownThemeColor, dropdownThemes } from './Dropdown.theme';

export const virtualScrollMinChildCount = 8;
export const virtualScrollChildHeight = 40;
export const virtualScrollContainerHeight = virtualScrollMinChildCount * virtualScrollChildHeight;

interface ContainerProps {
  $isVisible: boolean;
  $themeColor: DropdownThemeColor;
  $noPaddingTop?: boolean;
  $noPaddingBottom?: boolean;
}

export const Container = styled.div<ContainerProps>`
  z-index: 2147483632;
  display: flex;
  flex-direction: column;
  max-height: 80svh;
  padding: 12px 0;
  overflow: auto;
  background-color: ${props => props.theme.color[dropdownThemes[props.$themeColor].normal]};
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: 0 0 0 2px ${props => transparentize(0.95, props.theme.color[Color.Neutral_1000_Black])};
  transition: opacity 0.125s ease-in, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform: scale(${props => (props.$isVisible === true ? 1 : 0.85)});
  @media (${props => props.theme.breakpoint.sm.down}) {
    position: fixed !important;
    top: auto !important;
    right: 0 !important;
    bottom: 0 !important;
    left: 0 !important;
    border-radius: ${props => props.theme.borderRadius.large} ${props => props.theme.borderRadius.large} 0 0;
    transition: opacity 0.125s ease-in, transform 0.25s ease;
    transform: translateY(${props => (props.$isVisible === true ? 0 : '110%')});
  }
  @media (${props => props.theme.breakpoint.sm.up}) {
    opacity: ${props => (props.$isVisible === true ? 1 : 0)};
  }

  ${props =>
    props.$noPaddingTop &&
    css`
      padding-top: 0 !important;
    `};

  ${props =>
    props.$noPaddingBottom &&
    css`
      padding-bottom: 0 !important;
    `};

  &:focus-visible {
    outline: none;
    box-shadow: ${props => props.theme.focusBoxShadow} ${props => props.theme.color[Color.Blue_500_Ultramarine]};
  }
`;

export const SearchInputContainer = styled(StyledFormElements.Container)<{ $dropdownThemeColor: DropdownThemeColor }>`
  margin: 4px;
  background-color: ${props => props.theme.color[dropdownThemes[props.$dropdownThemeColor].normal]};
  border-color: ${props => props.theme.color[dropdownThemes[props.$dropdownThemeColor].hover]};

  &:hover {
    border-color: ${props => props.theme.color[dropdownThemes[props.$dropdownThemeColor].active]};
  }

  &:has(:focus-visible) {
    border-color: ${props => props.theme.color[dropdownThemes[props.$dropdownThemeColor].textChecked]};
  }
`;

interface VirtualScrollContainerProps {
  $noPaddingTop: boolean;
  $themeColor: DropdownThemeColor;
  $isScrollEnd: ('top' | 'bottom')[];
  $hasSearch: boolean;
  ref?: RefObject<HTMLDivElement>;
}

export const VirtualScrollContainer = styled.div<VirtualScrollContainerProps>`
  position: relative;
  width: 100%;
  max-height: 80svh;
  padding: 8px 0;
  overflow-y: auto;
  @media (${props => props.theme.breakpoint.sm.up}) {
    width: 240px;
    max-height: ${virtualScrollContainerHeight}px;
    max-height: 280px;
    padding: 4px 0;
  }

  ${props =>
    props.$hasSearch &&
    css`
      width: 100%;
    `};

  ${props =>
    props.$noPaddingTop &&
    css`
      padding-top: 0 !important;
    `};

  &::before,
  &::after {
    position: fixed;
    left: 0;
    z-index: 10000;
    width: 100%;
    height: 52px;
    pointer-events: none;
    content: '';
    transition: opacity 0.25s ease;
    transform: translate3d(0, 0, 0); // Fix stacking issues on iOS Safari
    @media (${props => props.theme.breakpoint.sm.up}) {
      height: 52px;
    }
  }

  &::before {
    top: ${props => (props.$hasSearch ? 52 : 0)}px;
    background: linear-gradient(
      to top,
      ${props => transparentize(1, props.theme.color[dropdownThemes[props.$themeColor].normal])} 0%,
      ${props => props.theme.color[dropdownThemes[props.$themeColor].normal]} 100%
    );
    opacity: ${props => (props.$isScrollEnd.includes('top') ? 0 : 1)};
  }

  &::after {
    bottom: 0;
    background: linear-gradient(
      to bottom,
      ${props => transparentize(1, props.theme.color[dropdownThemes[props.$themeColor].normal])} 0%,
      ${props => props.theme.color[dropdownThemes[props.$themeColor].normal]} 100%
    );
    opacity: ${props => (props.$isScrollEnd.includes('bottom') ? 0 : 1)};
  }
`;

export const VirtualScrollTotalHeightContainer = styled.div<{ $height: number }>`
  position: relative;
  width: 100%;
  height: ${props => props.$height}px;
`;

export const VirtualScrollPositionContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
`;

export const ErrorMessage = styled(StyledTypo.Typography)`
  margin: 20px auto 10px;
  text-align: center;
`;

export const BackgroundFade = styled.div<{ $isVisible: boolean }>`
  display: none;
  @media (${props => props.theme.breakpoint.sm.down}) {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 2147483631;
    display: block;
    width: 100%;
    height: 100%;
    pointer-events: none;
    background-color: ${props => props.theme.color[Color.Blue_1000_Deep]};
    opacity: ${props => (props.$isVisible === true ? 0.7 : 0)};
    transition: opacity ${props => (props.$isVisible === true ? '0.5s' : '0.3s')} ease;
  }
`;
